import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { To } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CLUB } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { cardAttributes } from '@sorare/core/src/lib/glossary';
import { Link } from '@sorare/core/src/routing/Link';

import { LevelBar } from 'components/user/progression/LevelBar';

import { TinyManagerId } from '../TinyManagerId';
import {
  CardManagerId_managerProgression,
  CardManagerId_user,
  CardManagerId_userSportProfile,
} from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-neutral-200);
`;
const StyledVertical = styled(Vertical)`
  flex: 1;
  overflow: hidden;
`;
const ellipsisCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StyledBodyM = styled(BodyM)`
  line-height: 20px;
  ${ellipsisCss}
`;
const StyledLabelS = styled(LabelS)`
  line-height: 14px;
  ${ellipsisCss}
`;
const StyledLevelBar = styled(LevelBar)`
  min-width: auto;
  height: var(--double-unit);
`;

const ConditionalLink = ({
  children,
  to,
  disableLink,
}: React.PropsWithChildren<Pick<Props, 'disableLink'> & { to: To }>) => {
  if (disableLink) {
    return <div>{children}</div>;
  }
  return <Link to={to}>{children}</Link>;
};

type Props = {
  user: CardManagerId_user;
  userSportProfile: CardManagerId_userSportProfile | null;
  managerProgression: CardManagerId_managerProgression | null;
  block?: boolean;
  disableLink?: boolean;
};

export const CardManagerId = ({
  user,
  managerProgression,
  userSportProfile,
  block,
  disableLink,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const Wrapper = block ? Root : Horizontal;

  if (!managerProgression) {
    return null;
  }

  const infos = (
    <SBHorizontal>
      <Vertical gap={0} style={{ overflow: 'hidden' }}>
        <StyledBodyM as="p" bold>
          {user.nickname}
        </StyledBodyM>
        <StyledLabelS as="p" color="var(--c-neutral-600)">
          {userSportProfile?.clubName}
        </StyledLabelS>
      </Vertical>
      {!disableLink && <FontAwesomeIcon icon={faChevronRight} />}
    </SBHorizontal>
  );

  const myClubLink = generateSportContextPath(ANY_SPORT_MY_CLUB, {
    params: { slug: user.slug },
  });

  const content = (
    <Wrapper>
      <TinyManagerId
        managerProgression={managerProgression}
        userSportProfile={userSportProfile}
      />
      <StyledVertical gap={1}>
        {block ? (
          infos
        ) : (
          <ConditionalLink to={myClubLink} disableLink={disableLink}>
            {infos}
          </ConditionalLink>
        )}
        <Horizontal>
          <LabelS as="p">
            <FormattedMessage {...cardAttributes.level} />
          </LabelS>
          <StyledLevelBar hideLevels managerProgression={managerProgression} />
        </Horizontal>
      </StyledVertical>
    </Wrapper>
  );

  if (!block) {
    return content;
  }

  return (
    <ConditionalLink to={myClubLink} disableLink={disableLink}>
      {content}
    </ConditionalLink>
  );
};

CardManagerId.fragments = {
  user: gql`
    fragment CardManagerId_user on PublicUserInfoInterface {
      id
      slug
      nickname
    }
  ` as TypedDocumentNode<CardManagerId_user>,
  userSportProfile: gql`
    fragment CardManagerId_userSportProfile on UserSportProfileInterface {
      id
      clubName
      ...TinyManagerId_userSportProfile
    }
    ${TinyManagerId.fragments.userSportProfile}
  ` as TypedDocumentNode<CardManagerId_userSportProfile>,
  managerProgression: gql`
    fragment CardManagerId_managerProgression on ManagerProgression {
      id
      ...TinyManagerId_managerProgression
      ...LevelBar_ManagerProgression
    }
    ${TinyManagerId.fragments.managerProgression}
    ${LevelBar.fragments.managerProgression}
  ` as TypedDocumentNode<CardManagerId_managerProgression>,
};
